/**
 * Layout utilities for workflow graphs using ELK (Eclipse Layout Kernel)
 * Provides smart, dynamic graph layouts that adapt to node dimensions
 */

/**
 * Apply ELK auto-layout to React Flow elements
 * @param {Array} nodes - React Flow nodes
 * @param {Array} edges - React Flow edges
 * @returns {Promise<{nodes, edges}>} - Layouted elements
 */
export async function getLayoutedElements(nodes, edges) {
  // Build adjacency map
  const outgoing = new Map();
  const incoming = new Map();
  
  edges.forEach(edge => {
    if (!outgoing.has(edge.source)) outgoing.set(edge.source, []);
    if (!incoming.has(edge.target)) incoming.set(edge.target, []);
    outgoing.get(edge.source).push(edge.target);
    incoming.get(edge.target).push(edge.source);
  });
  
  // Find entry point (node with no incoming edges)
  const entryNode = nodes.find(n => !incoming.has(n.id) || incoming.get(n.id).length === 0);
  
  // Manual layout for conditional branches
  const positioned = new Map();
  let currentY = 50;
  const centerX = 400;
  const verticalGap = 180;
  const horizontalSpread = 300;
  
  function layoutNode(nodeId, xOffset = 0) {
    if (positioned.has(nodeId)) return;
    
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    
    const x = centerX + xOffset;
    const y = currentY;
    
    positioned.set(nodeId, { x, y });
    
    const targets = outgoing.get(nodeId) || [];
    const isDecisionNode = node.type === 'decision';
    
    if (targets.length === 0) {
      // Leaf node
    } else if (targets.length === 1 && !isDecisionNode) {
      // Single path
      currentY += verticalGap;
      layoutNode(targets[0], xOffset);
    } else if (targets.length > 1 || isDecisionNode) {
      // Decision node or conditional branch - spread horizontally
      currentY += verticalGap;
      const branchY = currentY;
      
      targets.forEach((targetId, idx) => {
        const branchOffset = idx === 0 ? -horizontalSpread : horizontalSpread;
        positioned.set(targetId, { x: centerX + branchOffset, y: branchY });
        
        // Continue from branch
        const subTargets = outgoing.get(targetId) || [];
        if (subTargets.length > 0) {
          currentY = branchY + verticalGap;
          subTargets.forEach(subTarget => {
            if (!positioned.has(subTarget)) {
              layoutNode(subTarget, branchOffset);
            }
          });
        }
      });
    }
  }
  
  // Start layout
  if (entryNode) {
    layoutNode(entryNode.id);
  }

  // Fallback: any node the entry traversal didn't reach (disconnected nodes,
  // multiple roots, or graphs whose conditional/entry edges couldn't be
  // inferred) still needs a distinct, VISIBLE position — never leave it stacked
  // at the origin (which reads as an empty canvas). Stack the leftovers in the
  // central column below whatever the traversal already placed.
  nodes.forEach((node) => {
    if (!positioned.has(node.id)) {
      currentY += verticalGap;
      positioned.set(node.id, { x: centerX, y: currentY });
    }
  });

  // Apply positions
  const layoutedNodes = nodes.map(node => ({
    ...node,
    position: positioned.get(node.id) || { x: centerX, y: 50 }
  }));

  return {
    nodes: layoutedNodes,
    edges
  };
}
